import { Skeleton } from 'primeng/skeleton';
import { TranslatePipe } from '@ngx-translate/core';

import { NgTemplateOutlet } from '@angular/common';
import { input, output, computed, Component, TemplateRef, contentChild, ChangeDetectionStrategy } from '@angular/core';

import { Tag } from '@shared/ui/tag';
import { LoadingOverlay } from '@shared/ui/loading-overlay';
import { UserStatusTag } from '../../components/user-status-tag';
import { TableStateCard, type TableBusyState, type TableContentState } from '@shared/ui/table-state';
import {
  TABLE_IMPORTS,
  ACTIONS_COLUMN,
  TABLE_SORT_IMPORTS,
  type TableSortEvent,
  type TableSortDirection,
} from '@shared/ui/table';

import { type EntityAction } from '@shared/utils/entity-actions';
import { isTableRowActionEvent } from '@shared/utils/table-row-action-event';

import { type User, type UserOrderBy } from '../../models';

export interface UserTableSortChangeEvent {
  orderBy: UserOrderBy;
  orderDir: TableSortDirection;
}

const DATA_COLUMNS = ['name', 'email', 'role', 'status', 'firm'] as const;

const NO_DATA_TITLE_KEYS: Record<Exclude<TableContentState, 'rows'>, string> = {
  empty: 'entities.user.table.empty',
  noResults: 'common.table.noResults',
  error: 'entities.user.table.loadError',
};

@Component({
  selector: 'app-users-table',
  imports: [
    Tag,
    Skeleton,
    TABLE_IMPORTS,
    TranslatePipe,
    UserStatusTag,
    LoadingOverlay,
    TableStateCard,
    NgTemplateOutlet,
    TABLE_SORT_IMPORTS,
  ],
  templateUrl: './users-table.html',
  styleUrl: './users-table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersTable {
  public readonly items = input.required<User[]>();
  public readonly busyState = input<TableBusyState>('idle');
  public readonly contentState = input<TableContentState>('rows');
  public readonly skeletonRowsCount = input(15);

  public readonly orderBy = input<UserOrderBy | undefined>();
  public readonly orderDir = input<TableSortDirection | undefined>();
  public readonly rowActions = input<EntityAction<User>[]>([]);

  public readonly rowClick = output<User>();
  public readonly sortChange = output<UserTableSortChangeEvent>();

  protected readonly emptyTpl = contentChild('empty', { read: TemplateRef });
  protected readonly noResultsTpl = contentChild('noResults', { read: TemplateRef });
  protected readonly errorTpl = contentChild('error', { read: TemplateRef });

  protected readonly isInitialLoad = computed(() => this.busyState() === 'initialLoading');
  protected readonly rows = computed<User[] | unknown[]>(() =>
    this.isInitialLoad() ? Array.from({ length: this.skeletonRowsCount() }) : this.items(),
  );
  protected readonly showLoadingOverlay = computed(() => this.busyState() === 'reloading');
  protected readonly disableSorting = computed(() => this.busyState() !== 'idle');

  protected readonly defaultNoDataTitleKey = computed(
    () => NO_DATA_TITLE_KEYS[this.contentState() as Exclude<TableContentState, 'rows'>],
  );

  protected readonly displayedColumns = computed(() =>
    this.rowActions().length > 0 ? [...DATA_COLUMNS, ACTIONS_COLUMN] : DATA_COLUMNS,
  );

  protected onSortChange(event: TableSortEvent): void {
    this.sortChange.emit({
      orderBy: event.active as UserOrderBy,
      orderDir: event.direction,
    });
  }

  protected onRowClick(event: MouseEvent, user: User): void {
    if (isTableRowActionEvent(event) || this.isInitialLoad()) {
      return;
    }
    this.rowClick.emit(user);
  }
}
